import { useState } from 'react'

import { useTranslation, useVersion } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Modal } from '@molecule/app-ui-react'

import { APP_NAME, WEBSITE_URL } from '../branding.js'
import { loadContent } from '../config.js'
import { Icon } from './Icon.js'

export function Footer() {
  const cm = getClassMap()
  const { t, locale, setLocale, locales } = useTranslation()
  const { state: versionState } = useVersion()
  const [privacyOpen, setPrivacyOpen] = useState(false)
  const [termsOpen, setTermsOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)

  const version = versionState.version ? `v${versionState.version}` : 'v1.0.0'

  const openPrivacy = async () => {
    await loadContent('privacyPolicy')
    setPrivacyOpen(true)
  }

  const openTerms = async () => {
    await loadContent('termsOfService')
    setTermsOpen(true)
  }

  // Three centered, dot-separated GROUPS (deliberate related pairs instead of
  // one ragged wrap): Status · <language>, then Privacy · Terms, then About —
  // with the version pushed to the very bottom on its own. The old
  // `justify-around` + wrap spread six items into a random-looking grid in
  // narrow columns (the settings panel, the auth pages).
  const separator = (
    <span aria-hidden="true" style={{ opacity: 0.4, userSelect: 'none' }}>
      ·
    </span>
  )
  const groupStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 4,
  }

  return (
    <>
      <section style={{ width: '100%', padding: '12px 4px 0' }}>
        <div style={groupStyle}>
          <a
            href="https://status.molecule.dev"
            className={cm.footerLink}
            data-mol-id="footer-status-link"
          >
            {t('footer.status', undefined, { defaultValue: 'Status' })}
          </a>
          {separator}
          <button
            type="button"
            onClick={() => setLanguageOpen(true)}
            className={cm.footerButton}
            aria-label={t('footer.language')}
          >
            <Icon name="globe" size={14} /> {locales.find((l) => l.code === locale)?.name}
          </button>
        </div>
        <div style={groupStyle}>
          <button type="button" onClick={openPrivacy} className={cm.footerButton}>
            {t('footer.privacyPolicy')}
          </button>
          {separator}
          <button type="button" onClick={openTerms} className={cm.footerButton}>
            {t('footer.termsOfService')}
          </button>
        </div>
        <div style={groupStyle}>
          <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className={cm.footerLink}>
            {t('footer.about', { appName: APP_NAME })}
          </a>
        </div>
        <div
          className={cm.cn(cm.textSize('xs'), cm.textMuted)}
          style={{ textAlign: 'center', marginTop: 24, opacity: 0.7 }}
        >
          {version}
        </div>
      </section>
      <Modal
        open={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        title={t('footer.privacyPolicy')}
        size="lg"
      >
        <div
          className={cm.prose}
          dangerouslySetInnerHTML={{ __html: t('content.privacyPolicy', { appName: APP_NAME }) }}
        />
      </Modal>
      <Modal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        title={t('footer.termsOfService')}
        size="lg"
      >
        <div
          className={cm.prose}
          // appName must be passed (like the privacy modal above) or the
          // document renders its {{appName}} placeholders literally.
          dangerouslySetInnerHTML={{ __html: t('content.termsOfService', { appName: APP_NAME }) }}
        />
      </Modal>
      <Modal
        open={languageOpen}
        onClose={() => setLanguageOpen(false)}
        title={t('footer.language')}
        size="lg"
      >
        <div className={cm.languageGrid}>
          {locales
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={async () => {
                  await setLocale(l.code)
                  setLanguageOpen(false)
                }}
                className={l.code === locale ? cm.languageActive : cm.languageOption}
              >
                {l.name}
              </button>
            ))}
        </div>
      </Modal>
    </>
  )
}
